import cn from 'classnames';
import {
  memo,
  type DetailedHTMLProps,
  type HTMLAttributes,
  type LegacyRef,
  type ReactNode,
} from 'react';
import { MoreHoriz } from '@shared/assets';
import { IconButton } from '@shared/ui/buttons';
import { type IconButtonVariant } from '@shared/ui/buttons/icon-button/ui.tsx';
import * as st from './styles.module.scss';

interface ItemProps extends DetailedHTMLProps<HTMLAttributes<HTMLDivElement>, HTMLDivElement> {
  isActive?: boolean;
  disabled?: boolean;
}

export const DropdownItem = ({
  isActive,
  children,
  className,
  disabled,
  onClick,
  ...props
}: ItemProps) => {
  return (
    <div
      className={cn(st.item, { [st.active]: isActive }, disabled && st.disabled, className)}
      onClick={(e) => {
        e.stopPropagation();
        onClick?.(e);
      }}
      {...props}
    >
      {children}
    </div>
  );
};

export type MenuProps = Pick<
  DetailedHTMLProps<HTMLAttributes<HTMLDivElement>, HTMLDivElement>,
  'onMouseLeave'
> & {
  isMenuOpen: boolean;
  menuRef: LegacyRef<HTMLDivElement>;
  children: ReactNode;
  additionalChildren?: ReactNode;
  dropDownClassName?: string;
  wrapperClassName?: string;
  mouseLeaveAreaClassName?: string;
  smallButton?: boolean;
};

export const DropdownMenu = memo(
  ({
    isMenuOpen,
    menuRef,
    children,
    additionalChildren,
    dropDownClassName,
    wrapperClassName,
    mouseLeaveAreaClassName,
    onMouseLeave,
    smallButton,
  }: MenuProps): JSX.Element => {
    return (
      <div className={cn(st.wrapper, wrapperClassName)}>
        {additionalChildren}
        {isMenuOpen && (
          <>
            <div
              className={cn(
                st.dropdown,
                smallButton && st.dropdownWithSmallButton,
                dropDownClassName
              )}
              ref={menuRef}
              onMouseLeave={onMouseLeave}
            >
              <div
                className={cn(
                  st.dropdown,
                  smallButton && st.dropdownWithSmallButton,
                  // dropDownClassName,
                  st.mouseLeaveArea,
                  mouseLeaveAreaClassName
                )}
                ref={menuRef}
                onMouseLeave={onMouseLeave}
              ></div>
              {children}
            </div>
          </>
        )}
      </div>
    );
  }
);

export type DropdownMenuWithButtonPT = Omit<MenuProps, 'additionalChildren'> & {
  onMenuBtnClick: () => void;
  buttonVariant?: IconButtonVariant;
  buttonClassName?: string;
};

export const DropdownMenuWithButton = memo(
  ({
    onMenuBtnClick,
    buttonVariant,
    children,
    buttonClassName,
    smallButton,
    ...props
  }: DropdownMenuWithButtonPT): JSX.Element => {
    return (
      <DropdownMenu
        smallButton={smallButton}
        {...props}
        additionalChildren={
          <IconButton
            size={smallButton ? 'small' : undefined}
            variant={buttonVariant || (props.isMenuOpen ? 'activeDark' : 'transparent')}
            onClick={(e) => {
              e.stopPropagation();
              onMenuBtnClick();
            }}
            className={buttonClassName}
          >
            <MoreHoriz className={smallButton ? st.moreHorizSmall : undefined} />
          </IconButton>
        }
      >
        {children}
      </DropdownMenu>
    );
  }
);
